import { ConversionType, type AssetRef } from "#gen/bundles/v1/bundle_pb.js";
import { Mode } from "#gen/bundles/v1/service_pb.js";
import type { BundleDag } from "#lib/dag.js";

// True when an execute result came from submitting a conversion rather than following a plain nav
// button on a page that also has a form. A conversion trigger has no sourceAnchor, or a sourceAnchor
// equal to the page's conversion component. A plain nav button passes its own id, so it is not a
// submitted conversion even on a page that converts, and navigating past a form by a separate button
// is not gated.
export function isSubmittedConversion(sourceAnchor: string | undefined, conversionComponent: string): boolean {
	return sourceAnchor === undefined || (conversionComponent !== "" && sourceAnchor === conversionComponent);
}

export function shouldEmitAbandon(mode: Mode, conversionType: ConversionType, submitted: boolean): boolean {
	return mode === Mode.LIVE && conversionType !== ConversionType.UNSPECIFIED && !submitted;
}

// The single direct destination a plain nav anchor points at, when it is an unconditional, enabled
// link. Returns undefined for no match, more than one match, a link with conditions (geo, device or
// date rules the server evaluates and could reroute), or a disabled destination.
export function directLinkDestination(dag: BundleDag<AssetRef>, key: string, sourceAnchor: string | undefined): string | undefined {
	if (!sourceAnchor) {
		return undefined;
	}
	const links = dag.getNode(key)?.linksOut.filter(link => link.sourceAnchor === sourceAnchor) ?? [];
	if (links.length !== 1 || links[0].conditions.length > 0) {
		return undefined;
	}
	const destination = links[0].destination;
	if (dag.getNode(destination)?.ref.disabled) {
		return undefined;
	}
	return destination;
}

// The full page history entry a browser back or forward landed on. chainId is re-minted whenever the
// viewer drops the pushed chain and once per viewer instance, so an entry left behind by an earlier
// clear or by a previous document is recognisable rather than mistaken for a live one.
export interface FlowPageState {
	key: string;
	index: number;
	gated?: boolean;
	chainId?: string;
}

// What a browser back or forward should do, given the entry it landed on, the
// index currently shown, and the key of the last page the viewer itself pushed.
// index counts up per forward push, so a lower index than shown means back.
// - insync: the entry matches what is already shown, just resync.
// - bounce: forward onto a gated conversion result, which can't be restored, so step back to the form.
// - home: back reached the flow home, so tear the full page chain down.
// - load: land the page for this entry.
// An entry from a superseded chain is treated as home, because its index cannot be compared against
// the current one.
export type HistoryNavDecision = { action: "insync"; index: number } | { action: "bounce"; key: string; index: number } | { action: "home"; direction: "back" | "forward"; index: number } | { action: "load"; key: string; direction: "back" | "forward"; index: number };

export function decideHistoryNav(target: FlowPageState | undefined, currentIndex: number, appliedKey: string | undefined, currentChainId: string = ""): HistoryNavDecision {
	const key = target?.key;
	const index = target?.index ?? 0;
	// clearFlowPageHistory only rewrites the entry the user is standing on, so older entries survive
	// with their own index. Reading one of those as a forward nav would bounce a gated page or slide
	// the wrong way, so unwind to home instead. Checked before the insync echo below, because a stale
	// entry can carry the key that is currently applied.
	if (target && (target.chainId ?? "") !== currentChainId) {
		return { action: "home", direction: "back", index: 0 };
	}
	if (key === appliedKey) {
		return { action: "insync", index };
	}
	const direction: "back" | "forward" = index < currentIndex ? "back" : "forward";
	// Only forward onto a gated page bounces. Back onto an already converted page still restores it.
	if (direction === "forward" && target?.gated) {
		return { action: "bounce", key: target.key, index };
	}
	if (!key) {
		return { action: "home", direction, index };
	}
	return { action: "load", key, direction, index };
}
